import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  Check,
  Loader2,
  Route,
  UserCheck,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { autoScheduleAPI } from '@shared/apiClient';

const formatDayLabel = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

const fmtMin = (mins) => {
  if (mins == null) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

function flattenPreviewAssignments(days) {
  const assignments = [];
  days.forEach((day) => {
    day.techSlots.forEach((slot) => {
      slot.assignments.forEach((unit) => {
        unit.members.forEach((member) => {
          assignments.push({
            serviceCallId: member.serviceCallId,
            technicianId: slot.technicianId,
            scheduledDate: slot.date,
            typeId: member.typeId,
            displayName: member.displayName,
            originalDate: member.originalDate,
          });
        });
      });
    });
  });
  return assignments;
}

function groupKey(group) {
  return `${group.technicianId}|${group.date}`;
}

export default function IncompleteDayReviewModal({ isOpen, onClose }) {
  const queryClient = useQueryClient();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [step, setStep] = useState('list'); // list | choose | reschedule | squeeze
  const [reschedulePreview, setReschedulePreview] = useState(null);
  const [squeezeSelections, setSqueezeSelections] = useState({});
  const [squeezeData, setSqueezeData] = useState({});

  const { data, isLoading, refetch } = useQuery(
    ['incompleteTechDays'],
    async () => {
      const response = await autoScheduleAPI.getIncompleteDays();
      return response.data;
    },
    { enabled: isOpen, refetchInterval: isOpen ? 30000 : false }
  );

  const groups = data?.groups || [];

  useEffect(() => {
    if (!isOpen) {
      setSelectedGroup(null);
      setStep('list');
      setReschedulePreview(null);
      setSqueezeSelections({});
      setSqueezeData({});
    }
  }, [isOpen]);

  const acknowledgeMutation = useMutation(
    ({ technicianId, date }) => autoScheduleAPI.acknowledgeIncompleteDay({ technicianId, date }),
    {
      onSuccess: (response) => {
        toast.success(response.data.message || 'Left for technician');
        queryClient.invalidateQueries(['incompleteTechDays']);
        queryClient.invalidateQueries('calendarServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        resetToList();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to acknowledge');
      },
    }
  );

  const previewRescheduleMutation = useMutation(
    ({ technicianId, date }) => autoScheduleAPI.previewIncompleteDay({ technicianId, date }),
    {
      onSuccess: (response) => {
        setReschedulePreview(response.data.preview);
        setStep('reschedule');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Reschedule preview failed');
      },
    }
  );

  const applyMutation = useMutation(
    (assignments) => autoScheduleAPI.apply({ assignments }),
    {
      onSuccess: (response) => {
        const { applied, failed } = response.data;
        toast.success(`Scheduled ${applied} visit(s)${failed ? `, ${failed} failed` : ''}`);
        queryClient.invalidateQueries(['incompleteTechDays']);
        queryClient.invalidateQueries('schedulingBacklog');
        queryClient.invalidateQueries('calendarServiceCalls');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('mapPoints');
        onClose();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Apply failed');
      },
    }
  );

  const resetToList = () => {
    setSelectedGroup(null);
    setStep('list');
    setReschedulePreview(null);
    setSqueezeSelections({});
    setSqueezeData({});
  };

  const handleSelectGroup = (group) => {
    setSelectedGroup(group);
    setStep('choose');
  };

  const handleLeaveForTech = () => {
    if (!selectedGroup) return;
    if (!window.confirm(
      `Leave ${selectedGroup.calls.length} unfinished stop(s) for ${selectedGroup.technicianName} to decide when to complete?`
    )) return;
    acknowledgeMutation.mutate({
      technicianId: selectedGroup.technicianId,
      date: selectedGroup.date,
    });
  };

  const handleReschedulePreview = () => {
    if (!selectedGroup) return;
    if (selectedGroup.reschedulableCount === 0) {
      toast.error('No reschedulable stops on this day (locked or ineligible calls only)');
      return;
    }
    previewRescheduleMutation.mutate({
      technicianId: selectedGroup.technicianId,
      date: selectedGroup.date,
    });
  };

  const loadSqueezeCandidates = async (group) => {
    setStep('squeeze');
    const nextData = {};
    const nextSelections = {};
    await Promise.all(
      group.calls.map(async (call) => {
        try {
          const response = await autoScheduleAPI.getSqueezeCandidates({
            serviceCallId: call.serviceCallId,
          });
          nextData[call.serviceCallId] = response.data;
          const first = response.data.candidates?.[0];
          if (first) {
            nextSelections[call.serviceCallId] = `${first.technicianId}|${first.date}`;
          }
        } catch (error) {
          nextData[call.serviceCallId] = {
            serviceCallId: call.serviceCallId,
            displayName: call.displayName,
            candidates: [],
            reason: error.response?.data?.message || 'load_failed',
          };
        }
      })
    );
    setSqueezeData(nextData);
    setSqueezeSelections(nextSelections);
  };

  const rescheduleAssignments = useMemo(() => {
    if (!reschedulePreview?.days) return [];
    return flattenPreviewAssignments(reschedulePreview.days);
  }, [reschedulePreview]);

  const unplacedCount = reschedulePreview?.unplaceable?.length || 0;

  const squeezeReadyCount = useMemo(() => {
    if (!selectedGroup) return 0;
    return selectedGroup.calls.filter((call) => squeezeSelections[call.serviceCallId]).length;
  }, [selectedGroup, squeezeSelections]);

  const handleApplyReschedule = () => {
    if (rescheduleAssignments.length === 0) {
      toast.error('No placements in preview');
      return;
    }
    if (unplacedCount > 0) {
      if (!window.confirm(`${unplacedCount} stop(s) could not be placed. Apply the ${rescheduleAssignments.length} that were placed?`)) {
        return;
      }
    }
    applyMutation.mutate(
      rescheduleAssignments.map(({ serviceCallId, technicianId, scheduledDate, typeId }) => ({
        serviceCallId,
        technicianId,
        scheduledDate,
        typeId,
      }))
    );
  };

  const handleApplySqueeze = () => {
    const assignments = [];
    selectedGroup?.calls.forEach((call) => {
      const key = squeezeSelections[call.serviceCallId];
      if (!key) return;
      const entry = squeezeData[call.serviceCallId];
      const candidate = entry?.candidates?.find((c) => `${c.technicianId}|${c.date}` === key);
      if (!candidate) return;
      assignments.push({
        serviceCallId: call.serviceCallId,
        technicianId: candidate.technicianId,
        scheduledDate: candidate.date,
      });
    });
    if (assignments.length === 0) {
      toast.error('Select at least one squeeze-in slot');
      return;
    }
    applyMutation.mutate(assignments);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-end sm:items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-900/50" onClick={onClose} />
        <div className="relative w-full max-w-3xl bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2">
              {step !== 'list' && (
                <button
                  type="button"
                  onClick={resetToList}
                  className="p-1 rounded hover:bg-gray-100 text-gray-600"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              <div>
                <h2 className="text-base font-semibold text-gray-900">Incomplete Day Review</h2>
                {selectedGroup && step !== 'list' && (
                  <p className="text-xs text-gray-500">
                    {selectedGroup.technicianName} · {formatDayLabel(selectedGroup.date)}
                  </p>
                )}
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-auto p-4">
            {step === 'list' && (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Technicians who did not finish their assigned stops for a day appear here.
                  Choose how to handle each incomplete day.
                </p>
                {isLoading ? (
                  <div className="flex items-center justify-center py-12 text-sm text-gray-500 gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading incomplete days...
                  </div>
                ) : groups.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-500">
                    No incomplete tech-days need review.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {groups.map((group) => (
                      <button
                        key={groupKey(group)}
                        type="button"
                        onClick={() => handleSelectGroup(group)}
                        className="w-full text-left rounded-lg border border-gray-200 hover:border-amber-300 hover:bg-amber-50/40 px-3 py-3 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{group.technicianName}</div>
                            <div className="text-xs text-gray-600 mt-0.5">
                              {formatDayLabel(group.date)} · {group.calls.length} stop{group.calls.length === 1 ? '' : 's'} · {fmtMin(group.totalMinutes)} on-site
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {group.hasInProgress && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-2xs font-medium bg-blue-100 text-blue-800">
                                  In progress
                                </span>
                              )}
                              {group.hasLockedOrIneligible && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-2xs font-medium bg-amber-100 text-amber-800">
                                  Locked / ineligible
                                </span>
                              )}
                              {group.reschedulableCount < group.calls.length && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-2xs font-medium bg-gray-100 text-gray-700">
                                  {group.reschedulableCount} reschedulable
                                </span>
                              )}
                            </div>
                          </div>
                          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {step === 'choose' && selectedGroup && (
              <div className="space-y-4">
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                  <strong>{selectedGroup.calls.length}</strong> unfinished stop{selectedGroup.calls.length === 1 ? '' : 's'} from{' '}
                  <strong>{formatDayLabel(selectedGroup.date)}</strong>. Pick one action for this whole day.
                </div>

                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={handleReschedulePreview}
                    disabled={previewRescheduleMutation.isLoading || selectedGroup.reschedulableCount === 0}
                    className="w-full text-left rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50/40 px-4 py-4 disabled:opacity-50"
                  >
                    <div className="flex items-start gap-3">
                      <Route className="h-5 w-5 text-primary-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Reschedule optimally</div>
                        <p className="text-xs text-gray-600 mt-1">
                          Re-run route optimization for this tech&apos;s leftover stops and slot them into upcoming days around existing work.
                        </p>
                        {selectedGroup.reschedulableCount === 0 && (
                          <p className="text-xs text-amber-700 mt-1">No reschedulable stops on this day.</p>
                        )}
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={handleLeaveForTech}
                    disabled={acknowledgeMutation.isLoading}
                    className="w-full text-left rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 px-4 py-4 disabled:opacity-50"
                  >
                    <div className="flex items-start gap-3">
                      <UserCheck className="h-5 w-5 text-gray-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Leave for tech</div>
                        <p className="text-xs text-gray-600 mt-1">
                          No schedule change. The technician decides when to complete the leftover stops.
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => loadSqueezeCandidates(selectedGroup)}
                    className="w-full text-left rounded-lg border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/40 px-4 py-4"
                  >
                    <div className="flex items-start gap-3">
                      <CalendarClock className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Squeeze-in recommendations</div>
                        <p className="text-xs text-gray-600 mt-1">
                          See the best day and technician slot to fit each client into the current schedule with minimal added drive time.
                        </p>
                      </div>
                    </div>
                  </button>
                </div>

                <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {selectedGroup.calls.map((call) => (
                    <div key={call.serviceCallId} className="px-3 py-2 text-xs">
                      <div className="font-medium text-gray-900">{call.displayName}</div>
                      <div className="text-gray-500">{call.cityState || '—'} · {call.status}{call.scheduleLocked ? ' · locked' : ''}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {step === 'reschedule' && reschedulePreview && (
              <div className="space-y-4">
                {unplacedCount > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    {unplacedCount} stop(s) could not be placed within the lookahead window.
                  </div>
                )}
                {rescheduleAssignments.length === 0 ? (
                  <div className="text-sm text-gray-500 py-8 text-center">No placements generated.</div>
                ) : (
                  <div className="rounded-lg border border-gray-200 divide-y divide-gray-100">
                    {rescheduleAssignments.map((assignment) => (
                      <div key={assignment.serviceCallId} className="px-3 py-2.5 flex items-center justify-between gap-3 text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{assignment.displayName}</div>
                          <div className="text-xs text-gray-500">
                            From {formatDayLabel(selectedGroup.date)} → {formatDayLabel(assignment.scheduledDate)}
                          </div>
                        </div>
                        <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {step === 'squeeze' && selectedGroup && (
              <div className="space-y-4">
                {selectedGroup.calls.map((call) => {
                  const entry = squeezeData[call.serviceCallId];
                  const loading = !entry;
                  const candidates = entry?.candidates || [];
                  return (
                    <div key={call.serviceCallId} className="rounded-lg border border-gray-200 overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                        <div className="text-sm font-semibold text-gray-900">{call.displayName}</div>
                        <div className="text-xs text-gray-500">{call.cityState || '—'}</div>
                      </div>
                      <div className="p-3">
                        {loading ? (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Finding best slots...
                          </div>
                        ) : candidates.length === 0 ? (
                          <div className="text-xs text-amber-700">
                            {entry?.reason === 'needs_geocoding'
                              ? 'Client needs geocoding before squeeze-in can be calculated.'
                              : 'No feasible squeeze-in slot found in the lookahead window.'}
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            {candidates.map((candidate) => {
                              const key = `${candidate.technicianId}|${candidate.date}`;
                              const selected = squeezeSelections[call.serviceCallId] === key;
                              return (
                                <label
                                  key={key}
                                  className={clsx(
                                    'flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer text-xs',
                                    selected ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'
                                  )}
                                >
                                  <input
                                    type="radio"
                                    name={`squeeze-${call.serviceCallId}`}
                                    checked={selected}
                                    onChange={() => setSqueezeSelections((prev) => ({
                                      ...prev,
                                      [call.serviceCallId]: key,
                                    }))}
                                    className="mt-0.5"
                                  />
                                  <span>
                                    <span className="font-medium text-gray-900">{candidate.technicianName}</span>
                                    {' · '}
                                    {formatDayLabel(candidate.date)}
                                    {' · '}
                                    +{fmtMin(candidate.addedMinutes)} drive
                                    {' · '}
                                    {fmtMin(candidate.projectedDayTotalMinutes)} / {fmtMin(candidate.dayBudgetMinutes)} day
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={step === 'list' ? onClose : resetToList}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              {step === 'list' ? 'Close' : 'Back'}
            </button>

            <div className="flex items-center gap-2">
              {step === 'list' && (
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Refresh
                </button>
              )}
              {step === 'reschedule' && (
                <button
                  type="button"
                  onClick={handleApplyReschedule}
                  disabled={applyMutation.isLoading || rescheduleAssignments.length === 0}
                  className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {applyMutation.isLoading ? 'Applying...' : `Apply ${rescheduleAssignments.length} placement(s)`}
                </button>
              )}
              {step === 'squeeze' && (
                <button
                  type="button"
                  onClick={handleApplySqueeze}
                  disabled={applyMutation.isLoading || squeezeReadyCount === 0}
                  className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  {applyMutation.isLoading ? 'Applying...' : `Apply ${squeezeReadyCount} squeeze-in(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
